import html2canvas from 'html2canvas';
import { CapturedImage, ExportFooterData } from 'common/types/pdfExport';
import { Logger, syncSpreadsheetTitleContentRowHeightsInSubtree } from 'utilities';
import { collectExportWidgetData } from './collectExportWidgetData';

export type CaptureVerdict =
  | { kind: 'ready'; image: CapturedImage; footer?: ExportFooterData }
  | { kind: 'missing' }
  | { kind: 'failed'; reason?: string }
  | { kind: 'canceled' };

interface CapturePageArgs {
  url: string;
  exportTarget: string;
  signal: AbortSignal;
}

const CAPTURE_VIEWPORT_WIDTH_PX = 2600;
const CAPTURE_VIEWPORT_HEIGHT_PX = 5000;
const CAPTURE_VIEWPORT_MAX_WIDTH_PX = 6000;
const CAPTURE_VIEWPORT_WIDTH_PADDING_PX = 32;

const SKELETON_SELECTOR = [
  '[data-testid*="skeleton"]',
  '[data-testid*="Skeleton"]',
  '[data-testid*="loading"]',
  '[data-testid*="Loading"]',
  '.skeleton',
  '.loading-skeleton',
  '.shimmer',
  '[class*="skeleton"]',
  '[class*="Skeleton"]',
  '[class*="loading"]',
  '[class*="Loading"]',
  '[aria-label*="loading"]',
  '[aria-label*="Loading"]',
  '[aria-label*="skeleton"]',
  '[aria-label*="Skeleton"]',
].join(', ');

const CONTENT_ROW_SELECTOR = [
  'tbody tr',
  '[data-testid*="row"]',
  '[class*="cell"]:not([class*="header"])',
  '[class*="Cell"]:not([class*="Header"])',
].join(', ');
const MIN_CONTENT_TEXT_LENGTH = 20;

// Dev-only warning when a page reaches readiness via quiescence without any
// strict signal. Helps spot new `data-ref` sites that forgot `useExportSignal`.
const MISSING_SIGNAL_WARN_FRAMES = 600;

const hasVisibleSkeletonsInRoot = (root: HTMLElement | null): boolean => {
  if (!root) {
    return false;
  }
  const view = root.ownerDocument.defaultView;
  if (!view) {
    return false;
  }
  const nodes = root.querySelectorAll<HTMLElement>(SKELETON_SELECTOR);
  return Array.from(nodes).some(el => {
    const style = view.getComputedStyle(el);
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0' &&
      el.offsetWidth > 0 &&
      el.offsetHeight > 0
    );
  });
};

const createHiddenIframe = (url: string): HTMLIFrameElement => {
  const iframe = document.createElement('iframe');
  iframe.src = url;
  // Fixed in-viewport at max z-index to prevent Chrome from throttling
  // rAF/timers inside the iframe; opacity:0+pointer-events:none keeps it invisible.
  iframe.style.position = 'fixed';
  iframe.style.left = '0';
  iframe.style.top = '0';
  iframe.style.width = `${CAPTURE_VIEWPORT_WIDTH_PX}px`;
  iframe.style.height = `${CAPTURE_VIEWPORT_HEIGHT_PX}px`;
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.zIndex = '2147483647';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  return iframe;
};

const teardownIframe = (iframe: HTMLIFrameElement) => {
  try {
    // eslint-disable-next-line no-param-reassign
    iframe.src = 'about:blank';
  } catch {
    // ignore
  }
  try {
    iframe.remove();
  } catch {
    // ignore
  }
};

type ReadinessVerdict = 'ready' | 'missing' | { failed: true; reason?: string };
// `loading` is a non-terminal verdict: the page has explicitly told us it is
// still in flight, so we must not advance toward the missing fallback until
// that signal flips (or disappears).
type StrictObservation = ReadinessVerdict | 'loading';

// MISSING needs more frames than READY because "no target + no skeletons"
// is only conclusive after the page has stopped rendering.
const READY_STABILITY_FRAMES = 4;
const MISSING_STABILITY_FRAMES = 30;

// Backstop for a React app that's genuinely hung: no DOM mutations while
// status='loading'. Not the primary mechanism — MutationObserver + signals drive that.
const STALL_RECOVERY_MS = 30_000;

const bodyStatusAttrName = (target: string) => `data-export-status-${target.toLowerCase()}`;
const bodyErrorAttrName = (target: string) => `data-export-error-${target.toLowerCase()}`;

const hasMeaningfulContent = (targetEl: HTMLElement): boolean => {
  if (targetEl.children.length === 0) {
    return false;
  }
  if (targetEl.querySelector(CONTENT_ROW_SELECTOR)) {
    return true;
  }
  const text = targetEl.textContent?.trim() ?? '';
  return text.length >= MIN_CONTENT_TEXT_LENGTH;
};

const canResolveReady = (doc: Document, exportTarget: string): boolean => {
  const targetEl = doc.querySelector<HTMLElement>(`[data-ref="${exportTarget}"]`);
  if (!targetEl) {
    return false;
  }
  // Scope skeleton checks to the capture target only. Document-wide selectors
  // like `[class*="loading"]` match unrelated chrome (tabs, toolbars) and can
  // deadlock readiness while the owning section is already `data-export-ready`.
  return hasMeaningfulContent(targetEl) && !hasVisibleSkeletonsInRoot(targetEl);
};

/**
 * Waits for the iframe to reach a terminal state using an event-driven observer.
 * Resolves when a strict signal attribute appears, or when DOM quiescence plus
 * presence/absence of the target element points to a ready/missing verdict.
 */
const waitForReadiness = (
  iframe: HTMLIFrameElement,
  exportTarget: string,
  signal: AbortSignal
): Promise<ReadinessVerdict> =>
  new Promise<ReadinessVerdict>((resolve, reject) => {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      reject(new Error('Iframe document is not accessible'));
      return;
    }

    let resolved = false;
    let stableFrames = 0;
    let totalFrames = 0;
    let sawStrictSignal = false;
    let rafHandle: number | null = null;
    let mutationObserver: MutationObserver | null = null;
    let abortListener: (() => void) | null = null;
    let errorListener: ((ev: ErrorEvent) => void) | null = null;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;

    // Use the parent's rAF clock; the readiness checker only reads DOM attributes
    // and doesn't need the iframe to paint.
    const stopTicking = () => {
      if (rafHandle !== null) {
        cancelAnimationFrame(rafHandle);
        rafHandle = null;
      }
    };

    const clearStallTimer = () => {
      if (stallTimer !== null) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };

    const cleanup = () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
      }
      stopTicking();
      clearStallTimer();
      if (abortListener) {
        signal.removeEventListener('abort', abortListener);
        abortListener = null;
      }
      if (errorListener && win) {
        win.removeEventListener('error', errorListener);
        errorListener = null;
      }
    };

    const finish = (verdict: ReadinessVerdict) => {
      if (resolved) {
        return;
      }
      resolved = true;
      cleanup();
      resolve(verdict);
    };

    // Mutations reset the timer so only a truly quiet-but-still-loading iframe triggers the stall verdict.
    const armStallTimer = () => {
      clearStallTimer();
      stallTimer = setTimeout(() => {
        stallTimer = null;
        if (resolved) {
          return;
        }
        // A 429 retry is intentionally waiting — defer the stall verdict and
        // re-arm so the iframe gets a fresh 30s window once the wait ends.
        if (doc.body.hasAttribute('data-export-throttle-active')) {
          armStallTimer();
          return;
        }
        const currentStatus = doc.body.getAttribute(bodyStatusAttrName(exportTarget));
        if (currentStatus !== 'loading') {
          return;
        }
        Logger.warn(
          `pdfExport: waitForReadiness stalled for [data-ref="${exportTarget}"] — ` +
            `no DOM mutations and status="loading" for ${STALL_RECOVERY_MS}ms. Asking orchestrator to retry.`
        );
        finish({ failed: true, reason: 'stalled' });
      }, STALL_RECOVERY_MS);
    };

    const checkStrictSignals = (): StrictObservation | null => {
      // Body status may precede [data-ref] mounting; 'loading' blocks the quiescence-missing fallback.
      const bodyStatus = doc.body.getAttribute(bodyStatusAttrName(exportTarget));
      if (bodyStatus === 'loading') {
        sawStrictSignal = true;
        return 'loading';
      }

      const targetEl = doc.querySelector<HTMLElement>(`[data-ref="${exportTarget}"]`);
      if (targetEl) {
        if (targetEl.getAttribute('data-export-ready') === 'true') {
          sawStrictSignal = true;
          return 'ready';
        }
        if (targetEl.getAttribute('data-export-missing') === 'true') {
          sawStrictSignal = true;
          return 'missing';
        }
        if (targetEl.getAttribute('data-export-failed') === 'true') {
          sawStrictSignal = true;
          const reason = targetEl.getAttribute('data-export-error') || undefined;
          return { failed: true, reason };
        }
      }

      // Terminal body-level status (set before the element mounted — e.g.
      // the page decided the target is empty without ever rendering it).
      if (bodyStatus === 'missing') {
        sawStrictSignal = true;
        return 'missing';
      }
      if (bodyStatus === 'failed') {
        sawStrictSignal = true;
        const reason = doc.body.getAttribute(bodyErrorAttrName(exportTarget)) || undefined;
        return { failed: true, reason };
      }

      // Body-level failure signal is set by the orchestrator's own error
      // listener when the iframe's app crashes before a target can mount.
      if (doc.body.hasAttribute('data-export-failed')) {
        sawStrictSignal = true;
        const reason = doc.body.getAttribute('data-export-error') || undefined;
        return { failed: true, reason };
      }
      return null;
    };

    const tick = () => {
      rafHandle = requestAnimationFrame(() => {
        rafHandle = null;
        if (resolved) {
          return;
        }

        const strict = checkStrictSignals();
        if (strict === 'ready') {
          // A strict "ready" signal is necessary but not sufficient: only resolve
          // once DOM content is actually painted and skeletons are gone.
          if (canResolveReady(doc, exportTarget)) {
            finish('ready');
            return;
          }
        } else if (strict !== null && strict !== 'loading') {
          finish(strict);
          return;
        }
        // Explicit `loading` hold: stop ticking and wait for the next DOM
        // mutation (including the body-status attr flip) to re-evaluate.
        if (strict === 'loading') {
          return;
        }

        const targetEl = doc.querySelector<HTMLElement>(`[data-ref="${exportTarget}"]`);
        const skeletons = hasVisibleSkeletonsInRoot(targetEl);
        const hasRenderedTarget = !!targetEl && hasMeaningfulContent(targetEl);

        stableFrames += 1;
        totalFrames += 1;

        if (process.env.NODE_ENV !== 'production' && !sawStrictSignal && totalFrames === MISSING_SIGNAL_WARN_FRAMES) {
          Logger.warn(
            `pdfExport: no strict signal observed for [data-ref="${exportTarget}"] after ${MISSING_SIGNAL_WARN_FRAMES} frames. Falling back to DOM quiescence. Consider adding useExportSignal in the owning component.`
          );
        }

        if (hasRenderedTarget && !skeletons && stableFrames >= READY_STABILITY_FRAMES) {
          finish('ready');
          return;
        }

        // totalFrames (not reset by mutations) so continuous SPA re-renders can't
        // block the missing verdict indefinitely.
        if (!skeletons && totalFrames >= MISSING_STABILITY_FRAMES) {
          // Do not treat as missing while the page already asserted strict ready:
          // ScalarSpreadsheet and similar UIs can briefly fail `hasMeaningfulContent`
          // before rows paint; quiescence must not override that contract.
          if (targetEl?.getAttribute('data-export-ready') === 'true') {
            tick();
            return;
          }
          if (!targetEl || !hasRenderedTarget) {
            finish('missing');
            return;
          }
        }

        tick();
      });
    };

    const evaluate = () => {
      if (resolved) {
        return;
      }
      const strict = checkStrictSignals();
      if (strict === 'ready') {
        if (canResolveReady(doc, exportTarget)) {
          finish('ready');
          return;
        }
      } else if (strict !== null && strict !== 'loading') {
        finish(strict);
        return;
      }
      // While the page is explicitly loading, stay subscribed to mutations
      // and don't start the rAF-based missing-fallback counter.
      if (strict === 'loading') {
        return;
      }
      if (rafHandle === null) {
        tick();
      }
    };

    const onMutation = () => {
      if (resolved) {
        return;
      }
      stableFrames = 0;
      stopTicking();
      armStallTimer();
      evaluate();
    };

    if (signal.aborted) {
      finish({ failed: true, reason: 'canceled' });
      return;
    }

    abortListener = () => finish({ failed: true, reason: 'canceled' });
    signal.addEventListener('abort', abortListener);

    errorListener = (ev: ErrorEvent) => {
      Logger.warn('Iframe runtime error during capture', ev.message);
      if (!doc.body.hasAttribute('data-export-failed')) {
        doc.body.setAttribute('data-export-failed', 'true');
        doc.body.setAttribute('data-export-error', ev.message || 'iframe error');
      }
      evaluate();
    };
    win.addEventListener('error', errorListener);

    mutationObserver = new MutationObserver(onMutation);
    mutationObserver.observe(doc.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    armStallTimer();
    evaluate();
  });

// Crops uniform transparent margins from the captured canvas so that narrow
// content inside a wide container isn't scaled down to nothing by the PDF
// aspect-ratio fit. Returns the source unchanged when there's nothing
// meaningful to trim. Preserves the alpha channel so the PDF page background
// shows through behind the snapshot.
const TRIM_ALPHA_THRESHOLD = 8;
const TRIM_PADDING_PX = 16;
const trimCanvasWhitespace = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const { width, height } = source;
  if (width <= 0 || height <= 0) {
    return source;
  }
  const ctx = source.getContext('2d');
  if (!ctx) {
    return source;
  }

  let data: ImageData;
  try {
    data = ctx.getImageData(0, 0, width, height);
  } catch {
    // Tainted canvas — give up and return source.
    return source;
  }

  const px = data.data;
  const isContent = (i: number) => px[i + 3] > TRIM_ALPHA_THRESHOLD;

  const rowHasContent = (y: number): boolean => {
    const base = y * width * 4;
    for (let x = 0; x < width; x += 1) {
      if (isContent(base + x * 4)) {
        return true;
      }
    }
    return false;
  };

  const columnHasContent = (x: number, yStart: number, yEnd: number): boolean => {
    for (let y = yStart; y <= yEnd; y += 1) {
      if (isContent((y * width + x) * 4)) {
        return true;
      }
    }
    return false;
  };

  let top = 0;
  let bottom = height - 1;
  let left = 0;
  let right = width - 1;

  while (top < height && !rowHasContent(top)) {
    top += 1;
  }
  if (top >= height) {
    return source;
  } // fully blank, leave as-is
  while (bottom > top && !rowHasContent(bottom)) {
    bottom -= 1;
  }
  while (left < width && !columnHasContent(left, top, bottom)) {
    left += 1;
  }
  while (right > left && !columnHasContent(right, top, bottom)) {
    right -= 1;
  }

  const cropX = Math.max(0, left - TRIM_PADDING_PX);
  const cropY = Math.max(0, top - TRIM_PADDING_PX);
  const cropRight = Math.min(width, right + 1 + TRIM_PADDING_PX);
  const cropBottom = Math.min(height, bottom + 1 + TRIM_PADDING_PX);
  const cropWidth = cropRight - cropX;
  const cropHeight = cropBottom - cropY;

  if (cropWidth <= 0 || cropHeight <= 0) {
    return source;
  }
  if (cropX === 0 && cropY === 0 && cropWidth === width && cropHeight === height) {
    return source;
  }

  const trimmed = document.createElement('canvas');
  trimmed.width = cropWidth;
  trimmed.height = cropHeight;
  const trimmedCtx = trimmed.getContext('2d');
  if (!trimmedCtx) {
    return source;
  }
  trimmedCtx.drawImage(source, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
  return trimmed;
};

// Applies the same overflow fixes to the live iframe DOM that `onclone` applies
// to the html2canvas clone. Without this, `el.scrollWidth` is clipped by the
// scroll containers and we can't size the capture viewport correctly for wide
// spreadsheets (cap table, financials).
const UNCLIP_STYLES = { overflow: 'visible', overflowX: 'visible', overflowY: 'visible' };
const UNCLIP_STYLES_WITH_MAX_WIDTH = { ...UNCLIP_STYLES, maxWidth: 'none' };

const unclipScrollContainers = (root: HTMLElement) => {
  const simpleSelectors = [
    '[data-testid="native-scroll-wrapper"]',
    '[data-testid="native-scroll-content"]',
  ];
  simpleSelectors.forEach(selector => {
    root.querySelectorAll<HTMLElement>(selector).forEach(elem => {
      Object.assign(elem.style, UNCLIP_STYLES);
    });
  });
  root.querySelectorAll<HTMLElement>('#spreadsheet-table__content-columns').forEach(elem => {
    Object.assign(elem.style, UNCLIP_STYLES_WITH_MAX_WIDTH);
  });
};

// Waits for two animation frames so the app re-lays out after a DOM change.
const waitTwoFrames = (win: Window) =>
  new Promise<void>(resolve => {
    win.requestAnimationFrame(() => win.requestAnimationFrame(() => resolve()));
  });

// Re-queries [data-ref] after async operations to pick up any React remount that
// replaced the DOM node. Retries with rAF waits because React may briefly unmount
// the element (e.g. during a router navigate + setSelectedVersion sequence) and
// remount it on the next commit. Also re-applies unclip styles on the fresh node.
const resolveTargetEl = async (
  doc: Document,
  win: Window,
  exportTarget: string,
  previousEl: HTMLElement
): Promise<HTMLElement> => {
  const MAX_RETRIES = 20;
  let freshEl = doc.querySelector<HTMLElement>(`[data-ref="${exportTarget}"]`);
  for (let attempt = 0; !freshEl && attempt < MAX_RETRIES; attempt++) {
    // eslint-disable-next-line no-await-in-loop
    await waitTwoFrames(win);
    freshEl = doc.querySelector<HTMLElement>(`[data-ref="${exportTarget}"]`);
  }
  if (!freshEl) {
    throw new Error(`Target [data-ref="${exportTarget}"] was removed from the document and did not remount`);
  }
  if (freshEl !== previousEl) {
    unclipScrollContainers(freshEl);
    await waitTwoFrames(win);
  }
  return freshEl;
};

// Releases the GPU/painting memory backing a canvas. Setting width/height to 0
// drops the underlying bitmap and is critical when many large captures are
// produced in a single export — leaving them alive accumulates hundreds of MB
// of canvas memory and causes the browser to throttle later iframes.
const releaseCanvas = (canvas: HTMLCanvasElement) => {
  try {
    // eslint-disable-next-line no-param-reassign
    canvas.width = 0;
    // eslint-disable-next-line no-param-reassign
    canvas.height = 0;
  } catch {
    // ignore — best effort
  }
};

const captureElement = async (
  iframe: HTMLIFrameElement,
  exportTarget: string
): Promise<CapturedImage> => {
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    throw new Error('Iframe document is not accessible');
  }
  const el = doc.querySelector<HTMLElement>(`[data-ref="${exportTarget}"]`);
  if (!el) {
    throw new Error(`Target [data-ref="${exportTarget}"] not found`);
  }

  if ((doc as Document).fonts) {
    await (doc as Document).fonts.ready;
  }

  // Unclip scroll containers on the live DOM so scrollWidth reflects the real
  // content width before we size the capture viewport. The `onclone` hook still
  // runs inside html2canvas to re-apply these rules to the cloned render tree.
  unclipScrollContainers(el);
  await waitTwoFrames(win);

  // Equalise row heights between the title column and content column tables.
  // Must run after layout has settled (post-waitTwoFrames) so offsetHeight
  // reflects wrapped text, and before html2canvas clones the DOM.
  syncSpreadsheetTitleContentRowHeightsInSubtree(el);
  await waitTwoFrames(win);

  // Re-query after the async wait: React may have replaced the DOM node during
  // font loading or layout recalculations (e.g. a re-render that briefly
  // unmounts and remounts the component). Passing a stale/detached reference to
  // html2canvas causes "Unable to find element in cloned iframe".
  const currentEl = await resolveTargetEl(doc, win, exportTarget, el);

  const naturalWidth = Math.max(currentEl.scrollWidth, currentEl.offsetWidth);
  const captureWidth = Math.min(
    Math.max(CAPTURE_VIEWPORT_WIDTH_PX, naturalWidth + CAPTURE_VIEWPORT_WIDTH_PADDING_PX),
    CAPTURE_VIEWPORT_MAX_WIDTH_PX
  );

  if (captureWidth > CAPTURE_VIEWPORT_WIDTH_PX) {
    Object.assign(iframe.style, { width: `${captureWidth}px` });
    // One more rAF pair so the app re-lays out at the wider viewport before capture.
    await waitTwoFrames(win);
  }

  // Re-query one final time in case the wider viewport triggered another remount.
  const elToCapture = await resolveTargetEl(doc, win, exportTarget, currentEl);

  const raw = await html2canvas(elToCapture, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: null,
    windowWidth: captureWidth,
    windowHeight: Math.max(elToCapture.scrollHeight, elToCapture.offsetHeight),
    onclone: (_clonedDoc, clonedElement) => {
      unclipScrollContainers(clonedElement as HTMLElement);
      clonedElement.querySelectorAll('td, th').forEach(elem => {
        const htmlEl = elem as HTMLElement;
        if (htmlEl.style) {
          htmlEl.style.overflow = 'visible';
          htmlEl.style.overflowX = 'visible';
          htmlEl.style.overflowY = 'visible';
          htmlEl.style.textOverflow = 'clip';
          htmlEl.style.verticalAlign = 'middle';
        }
      });
      clonedElement.querySelectorAll('.cell, .text-cell').forEach(elem => {
        const htmlEl = elem as HTMLElement;
        if (htmlEl.style) {
          htmlEl.style.overflow = 'visible';
          htmlEl.style.overflowX = 'visible';
          htmlEl.style.overflowY = 'visible';
          htmlEl.style.textOverflow = 'clip';
          htmlEl.style.webkitLineClamp = 'unset';
        }
      });
      clonedElement.querySelectorAll('[id^="column-indicator-"]').forEach(elem => {
        const htmlElem = elem as HTMLElement;
        htmlElem.style.display = 'none';
      });
    },
  });

  // Trim uniform transparent margins so narrow content inside a wide container
  // isn't scaled down to nothing by the PDF aspect-ratio fit.
  const trimmed = trimCanvasWhitespace(raw);

  // Blobs avoid V8 heap pressure across captures; PDF assembler converts to data URL at addImage time.
  const blob = await new Promise<Blob>((resolve, reject) => {
    trimmed.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob returned null'))), 'image/png');
  });
  const image: CapturedImage = {
    blob,
    width: trimmed.width,
    height: trimmed.height,
    format: 'PNG',
  };
  if (trimmed !== raw) {
    releaseCanvas(raw);
  }
  releaseCanvas(trimmed);
  return image;
};

export const capturePage = async ({ url, exportTarget, signal }: CapturePageArgs): Promise<CaptureVerdict> => {
  if (signal.aborted) {
    return { kind: 'canceled' };
  }

  // Forwarded to Sentry breadcrumbs so a hung capture (DevTools-closed Chrome throttling) leaves a trace.
  Logger.info(`[capturePage] iframe-create   ${exportTarget}`);
  const iframe = createHiddenIframe(url);
  document.body.appendChild(iframe);

  let abortListener: (() => void) | null = null;

  try {
    await new Promise<void>((resolve, reject) => {
      if (signal.aborted) {
        reject(new Error('canceled'));
        return;
      }
      abortListener = () => reject(new Error('canceled'));
      signal.addEventListener('abort', abortListener);

      iframe.onload = () => {
        if (abortListener) {
          signal.removeEventListener('abort', abortListener);
        }
        abortListener = null;
        // Seed 'loading' before React mounts; without it quiescence fires at ~500ms before first effects run.
        if (iframe.contentDocument?.body) {
          iframe.contentDocument.body.setAttribute(bodyStatusAttrName(exportTarget), 'loading');
        }
        resolve();
      };
      iframe.onerror = () => {
        if (abortListener) {
          signal.removeEventListener('abort', abortListener);
        }
        abortListener = null;
        reject(new Error(`Failed to load ${url}`));
      };
    });
    Logger.info(`[capturePage] iframe-onload   ${exportTarget}`);

    if (signal.aborted) {
      return { kind: 'canceled' };
    }

    Logger.info(`[capturePage] readiness-start ${exportTarget}`);
    const verdict = await waitForReadiness(iframe, exportTarget, signal);
    Logger.info(`[capturePage] readiness-done  ${exportTarget} → ${typeof verdict === 'string' ? verdict : 'failed'}`);

    if (verdict === 'missing') {
      return { kind: 'missing' };
    }
    if (typeof verdict === 'object' && verdict.failed) {
      if (verdict.reason === 'canceled') {
        return { kind: 'canceled' };
      }
      return { kind: 'failed', reason: verdict.reason };
    }

    if (signal.aborted) {
      return { kind: 'canceled' };
    }

    // Fetched from the API by ExportWidgetRelay, not captured as a screenshot.
    const footer = await collectExportWidgetData(iframe, exportTarget, signal);

    if (signal.aborted) {
      return { kind: 'canceled' };
    }

    Logger.info(`[capturePage] capture-start   ${exportTarget}`);
    const image = await captureElement(iframe, exportTarget);
    if (!image || image.width <= 0 || image.height <= 0) {
      Logger.warn(`capturePage: empty capture for [data-ref="${exportTarget}"] (width=${image?.width ?? 0}, height=${image?.height ?? 0})`);
      return { kind: 'failed', reason: 'empty capture' };
    }
    Logger.info(`[capturePage] capture-done    ${exportTarget} (${image.blob.size} bytes)`);
    return { kind: 'ready', image, footer };
  } catch (err) {
    if (signal.aborted) {
      return { kind: 'canceled' };
    }
    Logger.error('capturePage failed', err);
    // html2canvas rejects with a plain string in some code paths, so check typeof
    // in addition to instanceof to surface the real message rather than 'unknown capture error'.
    let reason = 'unknown capture error';
    if (err instanceof Error) {
      reason = err.message;
    } else if (typeof err === 'string') {
      reason = err;
    }
    return { kind: 'failed', reason };
  } finally {
    if (abortListener) {
      signal.removeEventListener('abort', abortListener);
    }
    teardownIframe(iframe);
    Logger.info(`[capturePage] iframe-teardown ${exportTarget}`);
  }
};
